import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { FutebolUltimoEvento, FutebolJogoDia } from '@/shared/types';
import { soundEngine } from '@/shared/lib/soundEngine';
import { chaveGolDedup, placarDoTimeAvancou } from '../../domain/usecases/partida';

interface Props {
  evento:            FutebolUltimoEvento | null;
  jogo:              FutebolJogoDia | null;
  placarCasa:        number;
  placarVisitante:   number;
  initialKey?:       string | null;
  temaCopa?:         boolean;
}

const COPA_CORES = ['#009c3b', '#FFDF00', '#ffffff', '#009c3b', '#6ede8a', '#FFDF00', '#ffffff', '#002776', '#009c3b', '#FFDF00'];

const DURACAO_OVERLAY_MS  = 7_000;
const DURACAO_FADE_OUT_MS = 700;

// Onda 1 (0–0.2s) — amplitude total
const PARTICULAS_ONDA1 = [
  { x:  5, delay: 0.0, dur: 2.0, cor: '#fbbf24', size: 14, rot: 15  },
  { x: 12, delay: 0.05,dur: 1.8, cor: '#f87171', size: 10, rot: -20 },
  { x: 20, delay: 0.1, dur: 2.1, cor: '#34d399', size: 16, rot: 30  },
  { x: 30, delay: 0.0, dur: 1.9, cor: '#a78bfa', size: 11, rot: -10 },
  { x: 40, delay: 0.15,dur: 2.0, cor: '#fbbf24', size: 14, rot: 25  },
  { x: 50, delay: 0.05,dur: 1.7, cor: '#f87171', size: 12, rot: -30 },
  { x: 60, delay: 0.1, dur: 2.2, cor: '#34d399', size: 15, rot: 10  },
  { x: 70, delay: 0.0, dur: 1.8, cor: '#a78bfa', size: 10, rot: -15 },
  { x: 80, delay: 0.15,dur: 2.0, cor: '#fbbf24', size: 16, rot: 20  },
  { x: 90, delay: 0.05,dur: 1.9, cor: '#f87171', size: 11, rot: -25 },
];
// Onda 2 (0.3–0.5s) — posições intermediárias
const PARTICULAS_ONDA2 = [
  { x:  8, delay: 0.3, dur: 2.1, cor: '#fde68a', size: 12, rot: 18  },
  { x: 16, delay: 0.35,dur: 1.8, cor: '#6ee7b7', size:  9, rot: -22 },
  { x: 25, delay: 0.4, dur: 2.0, cor: '#c4b5fd', size: 13, rot: 28  },
  { x: 35, delay: 0.3, dur: 1.9, cor: '#fbbf24', size: 10, rot: -8  },
  { x: 45, delay: 0.45,dur: 2.2, cor: '#f87171', size: 14, rot: 22  },
  { x: 55, delay: 0.35,dur: 1.7, cor: '#34d399', size: 11, rot: -28 },
  { x: 65, delay: 0.4, dur: 2.1, cor: '#a78bfa', size: 13, rot: 12  },
  { x: 75, delay: 0.3, dur: 1.8, cor: '#fbbf24', size:  9, rot: -18 },
  { x: 85, delay: 0.45,dur: 2.0, cor: '#f87171', size: 12, rot: 24  },
  { x: 95, delay: 0.35,dur: 1.9, cor: '#34d399', size: 10, rot: -12 },
];
// Onda 3 (0.7–0.9s) — enchimento final
const PARTICULAS_ONDA3 = [
  { x:  3, delay: 0.7, dur: 2.2, cor: '#fde68a', size: 11, rot: 35  },
  { x: 22, delay: 0.75,dur: 2.0, cor: '#6ee7b7', size: 13, rot: -5  },
  { x: 42, delay: 0.8, dur: 1.8, cor: '#c4b5fd', size: 10, rot: 16  },
  { x: 62, delay: 0.7, dur: 2.1, cor: '#fbbf24', size: 14, rot: -32 },
  { x: 78, delay: 0.85,dur: 1.9, cor: '#f87171', size:  9, rot: 20  },
  { x: 92, delay: 0.75,dur: 2.0, cor: '#34d399', size: 12, rot: -15 },
  { x: 33, delay: 0.8, dur: 2.2, cor: '#a78bfa', size: 11, rot: 27  },
  { x: 52, delay: 0.7, dur: 1.7, cor: '#fbbf24', size: 13, rot: -20 },
  { x: 68, delay: 0.85,dur: 2.1, cor: '#f87171', size: 10, rot: 8   },
  { x: 88, delay: 0.75,dur: 1.8, cor: '#34d399', size: 14, rot: -28 },
];
const TODAS_PARTICULAS = [...PARTICULAS_ONDA1, ...PARTICULAS_ONDA2, ...PARTICULAS_ONDA3];

export default function FutebolGolOverlay({
  evento, jogo, placarCasa, placarVisitante, initialKey, temaCopa,
}: Props) {
  const [visivel,  setVisivel]  = useState(false);
  const [escudoErr, setEscudoErr] = useState(false);
  const prevKeyRef = useRef<string | null>(initialKey ?? null);
  const prevPlacarRef = useRef<{ casa: number; vis: number } | null>(null);

  const isGol    = evento?.tipo === 'gol';
  const eventoKey = isGol
    ? chaveGolDedup(evento!.time, placarCasa, placarVisitante)
    : null;

  useEffect(() => {
    if (!eventoKey || eventoKey === prevKeyRef.current) return;
    if (evento?.tipo === 'gol') {
      const prev = prevPlacarRef.current;
      const score = evento.time === 'casa' ? placarCasa : placarVisitante;
      if (prev && !placarDoTimeAvancou(evento.time, prev.casa, prev.vis, placarCasa, placarVisitante)) return;
      if (!prev && score <= 0) return;
    }
    prevKeyRef.current = eventoKey;
    soundEngine.playSound('gol');
    setEscudoErr(false);
    setVisivel(true);
    const hideTimer = setTimeout(() => setVisivel(false), DURACAO_OVERLAY_MS - DURACAO_FADE_OUT_MS);
    return () => clearTimeout(hideTimer);
    // Só a chave do gol recomeça o timer. CDC de placar/pitch no mesmo lance
    // não pode cancelar o dismiss — senão o overlay fica até o próximo evento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoKey]);

  useEffect(() => {
    prevPlacarRef.current = { casa: placarCasa, vis: placarVisitante };
  }, [placarCasa, placarVisitante]);

  const isVisible = visivel && !!evento && !!jogo;

  const timeQueMarkou = isVisible ? (evento!.time === 'casa' ? jogo!.time_casa : jogo!.time_visitante) : '';
  const escudoUrl     = isVisible ? (evento!.time === 'casa' ? jogo!.escudo_casa : jogo!.escudo_visitante) : null;
  const jogador       = isVisible && evento!.jogador !== '–' ? evento!.jogador : null;
  const minuto        = isVisible && evento!.minuto > 0 ? `${evento!.minuto}'` : null;

  const isBrasil  = !!temaCopa && timeQueMarkou.toLowerCase().includes('brasil');

  const accentColor   = isBrasil ? '#009c3b'
    : isVisible && evento!.time === 'casa' ? '#fbbf24' : '#38bdf8';
  const accentGlow    = isBrasil ? 'rgba(0,156,59,0.45)'
    : isVisible && evento!.time === 'casa'
      ? 'rgba(251,191,36,0.35)'
      : 'rgba(56,189,248,0.30)';
  const accentGrad    = isBrasil
    ? 'radial-gradient(ellipse at center, rgba(0,156,59,0.28) 0%, rgba(0,0,0,0) 70%)'
    : isVisible && evento!.time === 'casa'
      ? 'radial-gradient(ellipse at center, rgba(251,180,0,0.22) 0%, rgba(0,0,0,0) 70%)'
      : 'radial-gradient(ellipse at center, rgba(0,160,230,0.20) 0%, rgba(0,0,0,0) 70%)';
  const golTexto      = isBrasil ? '⚽ GOOOL DO BRASIL! 🇧🇷' : '⚽ Gol!';
  const particulasFinais = isBrasil
    ? TODAS_PARTICULAS.map((p, i) => ({ ...p, cor: COPA_CORES[i % COPA_CORES.length] }))
    : TODAS_PARTICULAS;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key={eventoKey}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center pointer-events-none select-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DURACAO_FADE_OUT_MS / 1000 }}
          style={{
            background: `radial-gradient(ellipse at center, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.65) 100%), ${accentGrad}`,
          }}
        >
          {/* Confetes com Framer Motion — stagger via delay por partícula */}
          {particulasFinais.map((p, i) => (
            <motion.div
              key={i}
              initial={{ y: 0, opacity: 0 }}
              animate={{ y: '110vh', opacity: [0, 1, 1, 0] }}
              transition={{ duration: p.dur, delay: p.delay, ease: 'linear' }}
              style={{
                position:     'fixed',
                top:          '-12px',
                left:         `${p.x}%`,
                width:        p.size,
                height:       p.size * 0.55,
                background:   p.cor,
                borderRadius: 2,
                rotate:       p.rot,
              }}
            />
          ))}

          {/* Brilho radial atrás do escudo */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position:     'absolute',
              width:        '18vw',
              height:       '18vw',
              borderRadius: '50%',
              background:   `radial-gradient(circle, ${accentGlow} 0%, transparent 70%)`,
            }}
          />

          {/* Escudo grande */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            style={{ marginBottom: '1.5vw' }}
          >
            {escudoUrl && !escudoErr ? (
              <img
                src={escudoUrl}
                alt={timeQueMarkou}
                onError={() => setEscudoErr(true)}
                style={{
                  width:      '12vw',
                  height:     '12vw',
                  minWidth:   120,
                  minHeight:  120,
                  objectFit:  'contain',
                  filter:     `drop-shadow(0 0 2vw ${accentGlow.replace('0.35','0.6').replace('0.30','0.55')})`,
                }}
              />
            ) : (
              <div
                className="rounded-full bg-white/10 flex items-center justify-center font-black uppercase text-white"
                style={{ width: '12vw', height: '12vw', minWidth: 120, minHeight: 120, fontSize: '3vw' }}
              >
                {timeQueMarkou.slice(0, 2)}
              </div>
            )}
          </motion.div>

          {/* GOL! */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', damping: 20 }}
            className="font-black uppercase tracking-widest"
            style={{
              fontSize:   '5vw',
              color:      accentColor,
              textShadow: `0 0 3vw ${accentGlow.replace('0.35', '0.8').replace('0.30', '0.75')}, 0 2px 8px rgba(0,0,0,0.8)`,
              lineHeight:  1,
            }}
          >
            {golTexto}
          </motion.div>

          {/* Nome do time */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
            className="font-bold"
            style={{ fontSize: '2vw', color: '#fff', marginTop: '0.5vw', opacity: 0.9 }}
          >
            {timeQueMarkou}
          </motion.div>

          {/* Jogador + minuto */}
          {(jogador || minuto) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45 }}
              style={{ fontSize: '1.4vw', color: 'rgba(255,255,255,0.55)', marginTop: '0.3vw' }}
            >
              {[jogador, minuto].filter(Boolean).join(' · ')}
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
